import React, {useState} from "react";
import {NavLink} from "react-router-dom";
import ContentBox from "../../../../../../components/content-box/ContentBox";
import PageActions from "../../../../../base/PageActions";
import Breadcrumbs from "../../../../../../components/breadcrumbs/Breadcrumbs";
import Modal from "../../../../../../components/modal/Modal";
import _image_service from "../../../../../../services/ImageService";
import '../../../../../styles/Common.scss';
import '../../WorldPages.scss';
import '../../WorldDynamicContent.scss';

interface IronHistoryPageProps {
}

const IronHistoryPage = (props: IronHistoryPageProps) => {

    const _map_src = `chase/world/geography/casarok_pre_cataclysm.png`;

    const page: PageActions = new PageActions({
        title: `Casarok History: Iron Age - Chase the Fox`,
        description: "The Iron Age of Casarok covers the time when life became more diverse, yet divided, in the world!",
        keywords: ["casarok", "casarok history", "casarok iron age", "fantasy timeline", "fantasy world history", "fantasy iron age"],
        imgSrc: _map_src,
        url: `world/history/iron-age`
    });

    const [modalIsOpen, setModalIsOpen] = useState<boolean>(false);

    return page.generateMetaContent(
        <>
            <Modal isOpen={modalIsOpen}
                   onClose={() => setModalIsOpen(false)}>
                <div className="geography-modal-view">
                    <p className="center">
                        <a href={_image_service.getCdnImageSrc(_map_src, 'full')}
                           target="_blank"
                           rel="noopener noreferrer">
                            Click here to view the full size image.
                        </a>
                    </p>
                    <img src={_image_service.getCdnImageSrc(_map_src, 'desktop')}
                         title='Casarok Pre-Cataclysm Map'
                         alt="A map of Casarok before the Great Cataclysm"/>
                </div>
            </Modal>
            <ContentBox header="The Iron Age of Casarok">
                <Breadcrumbs path={page.getUrl()}/>
                <div className="world-page-static">
                    <a id="visible-element"
                       className="switch-visible geography-modal-link"
                       onClick={() => setModalIsOpen(true)}>
                        <img className="block width-50-percent"
                             src={_image_service.getCdnImageSrc(`chase/world/geography/casarok_pre_cataclysm.png`)}
                             title='Casarok Pre-Cataclysm Map'
                             alt="A map of Casarok before the Great Cataclysm"/>
                    </a>
                </div>
                <div className="world-page-static">
                    <p className="center">
                        <em>This age covers the years of roughly 5,000 - 3,000 BCE.</em>
                    </p>
                    <p>
                        The Iron Age of Casarok encompasses the time during which all three races first lived in
                        Casarok. The <NavLink className="world-link" to="/world/bestiary/aspect">Aspect</NavLink> species
                        was created by the <NavLink className="world-link" to="/world/religion/pantheon">pantheon</NavLink> of
                        Casarok to provide a check to the unbridled power that
                        the <NavLink className="world-link" to="/world/bestiary/draconic">Draconic</NavLink> race has on
                        life on the planet.
                        The <NavLink className="world-link" to="/world/bestiary/anthro">Anthro</NavLink> race also
                        made its first appearance on Casarok later on in this time period, though their method of
                        appearance is unknown. Many scholars speculate about its link to the primordial beasts of the
                        previous age. The Draconic and Aspect races preyed upon the weaker Anthro race during this time,
                        however, by subjugating them.
                    </p>
                    <p>
                        Much of the oppression the Anthro race faced during this time was caused by the further
                        improvement of tools. Iron was the metal of choice for smithing weapons, armor, and other useful
                        items. Iron bonds were heavily in use as they proved difficult or impossible for ordinary
                        Anthros to break alone. The use of iron alongside the sudden appearance of other powerful
                        entities in the world also led to more conflicts. The land became even more fractured and
                        divided. The lands of most Draconic species were taken away, and many Draconics went into hiding
                        to protect themselves.
                        The <NavLink className="world-link" to="/world/bestiary/draconic/dracamma">dracammas</NavLink> were
                        an exception as they largely maintained their dominance in the world.
                    </p>
                    <p>
                        <em>How does Casarok's Iron Age differ from the iron age on Earth?</em><br/>
                        They are fairly similar aside from the use of magic and the pantheon's impact on the world.
                        Draconic and Aspect empires rose and fell as they did on Earth. Copper and bronze were still
                        widely in use, and it wasn't until later on that iron became the most prominent.
                    </p>
                    <h3 className="headline">Notable Events (in order of their occurrence)</h3>
                    <ul className="spaced">
                        <li>
                            The Aspect race is created by the gods and goddesses of Casarok, occupying their chosen
                            lands.
                        </li>
                        <li>
                            Iron weapons, armor, and tools are forged for the first time.
                        </li>
                        <li>
                            Territorial wars continue to occur among Draconic and Aspect races.
                        </li>
                        <li>
                            The Anthro race appears in the world. They try to take land for themselves, but they mostly
                            fail due to their relative lack of numbers and power.
                        </li>
                        <li>
                            Most Draconic species lose all or most of their land and must retreat to solitude.
                        </li>
                        <li>
                            The vast majority of Anthros are captured and enslaved with few free bastions existing.
                        </li>
                        <li>
                            The dracammas continue their dominance and take large portions of land for themselves,
                            forming some of the first large empires in the world.
                        </li>
                    </ul>
                </div>
            </ContentBox>
        </>
    );
}

export default IronHistoryPage;